import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep } from 'lodash';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Toolbar } from 'primereact/toolbar';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  FreshShipmentActions,
  FreshShipmentType,
} from '@/app/slices/freshShipment';
import { WithLocalStorage } from '@/components';
import useLocalStorage from '@/components/hooks/useLocalStorage';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  FertilizationQCResponse,
  ShipmentGroup,
  TOA_GetFreshShipments,
  TOA_GetFreshShipmentShipGroups,
} from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import {
  useFreshShipmentCacheInvalidation,
  useFreshShipmentListQuery,
  useFreshShipmentQCQuery,
  useShipGroupListQuery,
  useUpdateFreshShipmentTransaction,
} from '@/services/fresh-shipment/hooks';
import { useSiteByIdQuery } from '@/services/sites';
import { formatDateWithPattern } from '@/utils';
import { useContextMenu } from '@/utils/context-menu';
import { setLocalStorage } from '@/utils/storage';

import {
  BillableShippedDetailGrid,
  BillableShippedDetailGridParams,
} from './components/BillableShippedDetailGrid/index';
import { SelectedFert } from './components/BillableShippedDetailGrid/index';
import { FreshShipmentForm } from './components/FreshShipmentForm';
import { FreshShipmentSelector } from './components/FreshShipmentSelector';
import { FreshShipmentToolbar } from './components/FreshShipmentToolbar';
import {
  getFreshShipmentsColDefs,
  shipmentGroupsColDefs,
} from './utils/columnDefs';

import './index.css';

const PANEL_LEFT_SIZE = 50;
const PANEL_RIGHT_SIZE = 50;

type FreshShipmentProps = {
  className?: string;
};

export const FreshShipmentPage = ({ className }: FreshShipmentProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const isTrackingNumberUpdateEnabled = useFeatureFlag(
    'Tracking_Number_Update',
  );
  const shipmentGroupsGridRef = useRef<AgGridReact>(null);
  const signedOffShipmentGroupsGridRef = useRef<AgGridReact>(null);
  const freshShipmentGridRef = useRef<AgGridReact>(null);
  const ivfGridRef = useRef<AgGridReact>(null);
  const csrGridRef = useRef<AgGridReact>(null);
  const signedOffGridRef = useRef<AgGridReact>(null);

  const dispatch = useDispatch();

  const { showForm, isCsr, selectedShipmentGroups } = useSelector(
    (state: { freshShipment: FreshShipmentType }) => state.freshShipment,
  );
  const me = useAppSelector((state) => state.authentication.me);
  const [params] = useSearchParams();
  const date =
    params.get('date') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const siteId = params.get('sId');
  const isCsrParam = params.get('isCsr');
  const { data: site } = useSiteByIdQuery(siteId ?? undefined);
  const siteName = site?.name;
  const id = params.get('group-id');

  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [selectedShipmentGroupsLocal, setSelectedShipmentGroupsLocal] =
    useLocalStorage(`selectedShipmentGroups`, []);
  const [selectedFreshShipments, setSelectedFreshShipments] =
    useState<string[]>();
  const [freshShipments, setFreshShipments] = useState<TOA_GetFreshShipments[]>(
    [],
  );
  const selectedFertRef = useRef<SelectedFert[]>([]);
  const [signedOffShipmentGroups, setSignedOffShipmentGroups] =
    useState<TOA_GetFreshShipmentShipGroups[]>();
  const [activeShipmentGroups, setActiveShipmentGroups] =
    useState<TOA_GetFreshShipmentShipGroups[]>();
  const [fertQc, setFertQc] = useState<FertilizationQCResponse>();
  const [totals, setTotals] = useState({
    qual: 0,
    low: 0,
    ship: 0,
    estCount: 0,
    totalSelectedShipped: 0,
  });

  const { mutateAsync: updateFreshShipment } =
    useUpdateFreshShipmentTransaction();
  const invalidateCache = useFreshShipmentCacheInvalidation();
  const { data: shipmentGroupsData } = useShipGroupListQuery({
    fertilizationDate: date,
    fertilizationSiteId: siteId as string,
  });

  const { data: freshShipmentsData } = useFreshShipmentListQuery({
    fertilizationDate: date,
    fertilizationSiteId: siteId as string,
    fertilizationSiteName: siteName as string,
  });

  const activeGroups = shipmentGroupsData?.activeShipGroups ?? [];
  const signedOffGroups = shipmentGroupsData?.signedOffShipGroups ?? [];

  const hasGroups = activeGroups.length > 0 || signedOffGroups.length > 0;

  const shipGroupIds =
    activeGroups.length > 0
      ? activeGroups.map((g) => g.shipGroupId as string)
      : signedOffGroups.map((g) => g.shipGroupId as string);

  const { data: freshShipmentQcData } = useFreshShipmentQCQuery({
    shipGroupIds,
    fertilizationDate: date,
    fertilizationSiteId: siteId as string,
    skip: !siteId || !date || !shipmentGroupsData || !hasGroups,
  });
  const rowClassRules = useMemo(() => {
    return {
      'contains-exports': (params: {
        data?: { exportStatus?: boolean | null };
      }) => {
        return params.data?.exportStatus === true;
      },
    };
  }, []);

  const freshShipmentsColDefs = useMemo(() => getFreshShipmentsColDefs(), []);

  useEffect(() => {
    if (!id || !shipmentGroupsData) {
      dispatch({
        type: FreshShipmentActions.UPDATE_SHOW_FORM,
        showForm: false,
      });
      return;
    }
    dispatch({
      type: FreshShipmentActions.UPDATE_SHOW_FORM,
      showForm: true,
    });
  }, [dispatch, id, shipmentGroupsData]);

  useEffect(() => {
    if (shipmentGroupsData) {
      setSignedOffShipmentGroups(shipmentGroupsData.signedOffShipGroups);
      setActiveShipmentGroups(shipmentGroupsData.activeShipGroups);
    }
  }, [shipmentGroupsData]);

  useEffect(() => {
    if (!id) {
      setLocalStorage(
        'freshShipment',
        JSON.stringify([]),
        'selectedFreshShipmentRows',
      );
    }
  }, [dispatch, id, params]);

  useEffect(() => {
    if (showForm === false) {
      invalidateCache([
        'FreshShipmentList',
        'QcFreshShipment',
        'ShipGroupList',
        'FertilizationListFreshShipment',
      ]);
    }
  }, [invalidateCache, showForm]);

  useEffect(() => {
    dispatch({
      type: FreshShipmentActions.UPDATE_IS_CSR,
      isCsr: isCsrParam === 'true',
    });
  }, [dispatch, isCsrParam, me.groups]);

  useEffect(() => {
    if (freshShipmentsData) {
      setFreshShipments(cloneDeep(freshShipmentsData));
    } else {
      setFreshShipments([]);
    }
  }, [freshShipmentsData]);

  const checkQcLoaded = useCallback(
    (fertQc: FertilizationQCResponse) => {
      if (fertQc?.qcShippingTubeExpirationDate !== null) {
        dispatch({
          type: FreshShipmentActions.UPDATE_QC_LOADED,
          qcLoaded: true,
        });
      } else {
        dispatch({
          type: FreshShipmentActions.UPDATE_QC_LOADED,
          qcLoaded: false,
        });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (freshShipmentQcData) {
      checkQcLoaded(freshShipmentQcData);
      setFertQc(freshShipmentQcData);
    }
  }, [checkQcLoaded, freshShipmentQcData]);

  const handleUpdateFreshShipment = async (
    params: CellValueChangedEvent & { newValue?: string },
  ) => {
    const field = params.column.getColDef().field as string;
    if (field === 'verified') return;
    const value =
      params.newValue !== undefined ? params.newValue : params.data[field];

    await updateFreshShipment({
      id: params.data.freshShipmentId,
      field,
      value,
    });

    if (isTrackingNumberUpdateEnabled && field === 'trackingNumber') {
      params.data[field] = value;
      params.api.refreshCells({
        rowNodes: [params.node],
        columns: [field],
        force: true,
      });
      return;
    }
  };

  const handleSelectionChanged = (): void => {
    const activeSelectedRows =
      shipmentGroupsGridRef.current?.api.getSelectedRows() || [];
    const signedOffSelectedRows =
      signedOffShipmentGroupsGridRef.current?.api.getSelectedRows() || [];

    const combinedSelectedRows = [
      ...activeSelectedRows,
      ...signedOffSelectedRows,
    ];

    setSelectedShipmentGroupsLocal(
      (combinedSelectedRows as TOA_GetFreshShipmentShipGroups[]).map(
        (node) => ({
          id: node.shipGroupId as string,
          value: node.shipGroupName as string,
        }),
      ),
    );

    dispatch({
      type: FreshShipmentActions.SET_SELECTED_SHIPMENT_GROUPS,
      selectedShipmentGroups: (
        combinedSelectedRows as TOA_GetFreshShipmentShipGroups[]
      ).map((node) => ({
        id: node.shipGroupId as string,
        value: node.shipGroupName as string,
      })),
    });
  };

  const handleFreshShipmentSelectionChanged = () => {
    const selectedRows = freshShipmentGridRef.current?.api.getSelectedRows();
    setSelectedFreshShipments(
      (selectedRows as TOA_GetFreshShipments[]).map(
        (node) => node.freshShipmentId,
      ) as string[],
    );
  };

  const { getContextMenuItems: shipmentGroupsContextMenu } = useContextMenu({
    idKey: 'shipGroupId',
  });
  const { getContextMenuItems: freshShipmentContextMenu } = useContextMenu({
    idKey: 'freshShipmentId',
  });

  return (
    <div
      className={`fresh-shipment common-page${className ? ` ${className}` : ''}`}
    >
      <>
        <Toolbar
          className="toolbar-above-panel"
          start={
            <FreshShipmentSelector
              className="fresh-shipment__selector"
              checkQcLoaded={checkQcLoaded}
              fertQc={fertQc as FertilizationQCResponse}
              setSelectedShipmentGroupsLocal={setSelectedShipmentGroupsLocal}
              totals={totals}
            />
          }
          end={
            <FreshShipmentToolbar
              ivfGridRef={ivfGridRef}
              csrGridRef={csrGridRef}
              signedOffGridRef={signedOffGridRef}
              freshShipmentGridRef={freshShipmentGridRef}
              selectedFertRef={selectedFertRef}
              fertQcData={fertQc as FertilizationQCResponse}
              quickSearchValue={quickSearchValue}
              setQuickSearchValue={setQuickSearchValue}
              selectedShipmentGroups={selectedShipmentGroups}
              signedOffShipmentGroupIds={
                signedOffShipmentGroups?.map(
                  (group) => group.shipGroupId as string,
                ) as string[]
              }
              checkQcLoaded={checkQcLoaded}
              fertQc={fertQc as FertilizationQCResponse}
            />
          }
        />

        {!showForm ? (
          <Splitter className="fresh-shipment__grid-container">
            <SplitterPanel
              className="fresh-shipment__grid1"
              size={PANEL_LEFT_SIZE}
            >
              <div className="shipment-groups-grid">
                <WithLocalStorage storageKey="shipment-groups-grid">
                  <AgGridReact
                    gridOptions={defaultGridOptions}
                    ref={shipmentGroupsGridRef}
                    getContextMenuItems={shipmentGroupsContextMenu}
                    className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}${className ? ` ${className}` : ''} with-vertical-lines`}
                    rowData={activeShipmentGroups}
                    defaultColDef={defaultColDef}
                    columnDefs={shipmentGroupsColDefs}
                    rowSelection="multiple"
                    onSelectionChanged={handleSelectionChanged}
                    onGridReady={(params) => {
                      params.api.forEachNode((node) => {
                        if (
                          selectedShipmentGroupsLocal.some(
                            (group: ShipmentGroup) =>
                              group.id === node.data.shipGroupId,
                          )
                        ) {
                          node.setSelected(true);
                        }
                      });
                    }}
                  />
                </WithLocalStorage>
              </div>
              <div className="signed-off-grid">
                <WithLocalStorage storageKey="signed-off-grid">
                  <AgGridReact
                    gridOptions={defaultGridOptions}
                    ref={signedOffShipmentGroupsGridRef}
                    className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}${className ? ` ${className}` : ''} with-vertical-lines`}
                    getContextMenuItems={shipmentGroupsContextMenu}
                    rowData={signedOffShipmentGroups}
                    defaultColDef={defaultColDef}
                    columnDefs={shipmentGroupsColDefs}
                    rowSelection="multiple"
                    onSelectionChanged={handleSelectionChanged}
                    onGridReady={(params) => {
                      params.api.hideOverlay();
                      params.api.forEachNode((node) => {
                        if (
                          selectedShipmentGroupsLocal.some(
                            (group: ShipmentGroup) =>
                              group.id === node.data.shipGroupId,
                          )
                        ) {
                          node.setSelected(true);
                        }
                      });
                    }}
                  />
                </WithLocalStorage>
              </div>
            </SplitterPanel>
            <SplitterPanel
              className="fresh-shipment__grid2"
              size={PANEL_RIGHT_SIZE}
            >
              <WithLocalStorage storageKey="fresh-shipments-grid">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  ref={freshShipmentGridRef}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}${className ? ` ${className}` : ''} with-vertical-lines`}
                  getContextMenuItems={freshShipmentContextMenu}
                  rowData={freshShipments}
                  rowClassRules={rowClassRules}
                  defaultColDef={defaultColDef}
                  masterDetail={true}
                  detailCellRenderer={BillableShippedDetailGrid}
                  detailRowAutoHeight={true}
                  detailCellRendererParams={
                    {
                      selectedFertRef,
                    } as Partial<BillableShippedDetailGridParams>
                  }
                  columnDefs={freshShipmentsColDefs}
                  overlayLoadingTemplate="Please Select a Site and a Technician"
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onCellValueChanged={handleUpdateFreshShipment}
                  onSelectionChanged={handleFreshShipmentSelectionChanged}
                  context={{
                    handleUpdateFreshShipment,

                    isCsr: isCsr,
                    isTrackingNumberUpdateEnabled,
                    selectedFreshShipments: selectedFreshShipments,
                  }}
                />
              </WithLocalStorage>
            </SplitterPanel>
          </Splitter>
        ) : (
          <>
            <FreshShipmentForm
              gridRef={ivfGridRef}
              csrGridRef={csrGridRef}
              signedOffGridRef={signedOffGridRef}
              quickSearchValue={quickSearchValue}
              setTotals={setTotals}
              signedOffShipmentGroupIds={
                signedOffShipmentGroups?.map(
                  (group) => group.shipGroupId as string,
                ) as string[]
              }
            ></FreshShipmentForm>
          </>
        )}
      </>
    </div>
  );
};
